/**
 * useReports Hook
 * React Query hook for report generation, listing, and download.
 * Includes polling that stops when the latest report completes or fails.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useTenant } from "@/contexts/TenantContext";
import { ReportType } from "@app/types";

interface Report {
  id: string;
  reportType: ReportType | null;
  format: string;
  status: "pending" | "generating" | "completed" | "failed";
  fileSize: number | null;
  generatedAt: string;
  completedAt: string | null;
}

interface ListReportsResponse {
  reports: Report[];
}

interface GenerateReportResponse {
  jobId: string;
  reportIds: string[];
  message: string;
}

interface DownloadResponse {
  downloadUrl: string;
  expiresIn: number;
}

export interface UseReportsOptions {
  tenantIdOverride?: string;
  /** When true, requests only the most recent completed report per type */
  latest?: boolean;
}

export interface UseReportsResult {
  reports: Report[];
  latestReport: Report | null;
  investmentMemorandum: Report | null;
  technicalAssessment: Report | null;
  isLoadingReports: boolean;
  reportsError: Error | null;
  isReportInProgress: boolean;
  generateReport: () => Promise<GenerateReportResponse>;
  isGenerating: boolean;
  generateError: Error | null;
  downloadReport: (reportId: string) => Promise<void>;
}

export function useReports(
  assessmentId: string,
  options?: UseReportsOptions,
): UseReportsResult {
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();

  const tenantId = options?.tenantIdOverride ?? currentTenant?.id;
  const isReadOnly = !!options?.tenantIdOverride;

  // Query: List reports with polling
  const {
    data: reportsData,
    isLoading: isLoadingReports,
    error: reportsError,
  } = useQuery<ListReportsResponse, Error>({
    queryKey: ["reports", tenantId, assessmentId, options?.latest ?? false],
    queryFn: async () => {
      if (!tenantId) throw new Error("No tenant selected");

      const latestParam = options?.latest ? "?latest=true" : "";
      const url = `/tenants/${tenantId}/assessments/${assessmentId}/reports${latestParam}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await apiClient.GET(url as any);
      if (error) throw new Error("Failed to fetch reports");
      return data as ListReportsResponse;
    },
    enabled: !!tenantId && !!assessmentId,
    // Poll every 3 seconds while any report is in-progress (skip for read-only)
    refetchInterval: isReadOnly
      ? false
      : (query) => {
          const reports = query.state.data?.reports || [];
          const hasInProgress = reports.some(
            (r) => r.status === "pending" || r.status === "generating",
          );
          return hasInProgress ? 3000 : false;
        },
    refetchIntervalInBackground: false,
    staleTime: (query) => {
      const reports = query.state.data?.reports || [];
      const hasInProgress = reports.some(
        (r) => r.status === "pending" || r.status === "generating",
      );
      return hasInProgress ? 0 : 5 * 60 * 1000;
    },
  });

  // Mutation: Trigger report generation
  const {
    mutateAsync: generateReport,
    isPending: isGenerating,
    error: generateError,
  } = useMutation<GenerateReportResponse, Error>({
    mutationFn: async () => {
      if (!tenantId) throw new Error("No tenant selected");
      if (isReadOnly)
        throw new Error("Cannot generate report in read-only mode");

      const url = `/tenants/${tenantId}/assessments/${assessmentId}/reports`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await apiClient.POST(url as any);

      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const msg = (error as any)?.message || "Failed to generate report";
        throw new Error(msg);
      }
      return data as GenerateReportResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reports", tenantId, assessmentId],
      });
    },
  });

  // Helper: download the latest completed report
  const downloadReport = async (reportId: string) => {
    if (!tenantId) throw new Error("No tenant selected");

    const url = `/tenants/${tenantId}/assessments/${assessmentId}/reports/${reportId}/download`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await apiClient.GET(url as any);
    if (error) throw new Error("Failed to get download URL");

    const { downloadUrl } = data as DownloadResponse;
    window.open(downloadUrl, "_blank");
  };

  const reports = reportsData?.reports || [];

  const sortedReports = [...reports].sort(
    (a, b) =>
      new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime(),
  );

  // The download affordances surface the most-recent *completed* export of each
  // type, so a newer failed attempt can't mask an older successful render.
  //
  // NOTE: failure detection (`reports.some((r) => r.status === "failed")`) is
  // ONLY reliable when `UseReportsOptions.latest` is **not** set. The server's
  // `latest=true` endpoint filters to completed exports, which hides failures.
  // Callers that need to know about failed exports must leave `latest`
  // undefined (or use a separate non-latest query for that signal).
  const investmentMemorandum =
    sortedReports.find(
      (r) =>
        r.reportType === ReportType.INVESTMENT_MEMORANDUM &&
        r.status === "completed",
    ) ?? null;
  const technicalAssessment =
    sortedReports.find(
      (r) =>
        r.reportType === ReportType.TECHNICAL_ASSESSMENT &&
        r.status === "completed",
    ) ?? null;

  const isReportInProgress = sortedReports.some(
    (r) => r.status === "pending" || r.status === "generating",
  );

  const latestReport = investmentMemorandum ?? technicalAssessment ?? null;

  return {
    reports,
    latestReport,
    investmentMemorandum,
    technicalAssessment,
    isLoadingReports,
    reportsError,
    isReportInProgress,

    generateReport,
    isGenerating,
    generateError,

    downloadReport,
  };
}
